import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useFriendsList } from '../hooks/useFriendsList';
import { createBout } from '../services/boutService';
import { BOUT_TARGETS, BOUT_VISIBILITY } from '../utils/constants';
import './CreateBoutForm.css';

export default function CreateBoutForm({ onClose, onCreated }) {
  const [visibility, setVisibility] = useState(BOUT_VISIBILITY.PUBLIC);
  const [targetStreak, setTargetStreak] = useState(5);
  const [selectedFriend, setSelectedFriend] = useState('');
  const [creating, setCreating] = useState(false);
  const { friends, loading: friendsLoading } = useFriendsList();

  const handleCreate = async () => {
    if (visibility === BOUT_VISIBILITY.PRIVATE && !selectedFriend) {
      toast.error('Select a friend to invite');
      return;
    }

    setCreating(true);
    try {
      const invitedUsername = visibility === BOUT_VISIBILITY.PRIVATE ? selectedFriend : null;
      await createBout(visibility, targetStreak, invitedUsername);
      toast.success('Bout created!');
      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      const message = err?.message || 'Failed to create bout';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="create-bout-overlay" onClick={onClose}>
      <div className="create-bout-form" onClick={(e) => e.stopPropagation()}>
        <div className="create-bout-header">
          <h2>Create a Bout</h2>
          <button className="create-bout-close" onClick={onClose}>&times;</button>
        </div>

        {/* Visibility Toggle */}
        <div className="create-bout-section">
          <label className="create-bout-label">Visibility</label>
          <div className="create-bout-toggle">
            <button
              className={`toggle-btn ${visibility === BOUT_VISIBILITY.PUBLIC ? 'active' : ''}`}
              onClick={() => { setVisibility(BOUT_VISIBILITY.PUBLIC); setSelectedFriend(''); }}
            >
              Public
            </button>
            <button
              className={`toggle-btn ${visibility === BOUT_VISIBILITY.PRIVATE ? 'active' : ''}`}
              onClick={() => setVisibility(BOUT_VISIBILITY.PRIVATE)}
            >
              Private
            </button>
          </div>
          <p className="create-bout-hint">
            {visibility === BOUT_VISIBILITY.PUBLIC
              ? 'Anyone can join from the public lobby'
              : 'Only your invited friend can join'}
          </p>
        </div>

        {/* Target Streak */}
        <div className="create-bout-section">
          <label className="create-bout-label">First to Streak of...</label>
          <div className="create-bout-targets">
            {BOUT_TARGETS.map((t) => (
              <button
                key={t}
                className={`target-btn ${targetStreak === t ? 'active' : ''}`}
                onClick={() => setTargetStreak(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Friend Picker (private only) */}
        {visibility === BOUT_VISIBILITY.PRIVATE && (
          <div className="create-bout-section">
            <label className="create-bout-label">Invite a Friend</label>
            {friendsLoading ? (
              <p className="create-bout-hint">Loading friends...</p>
            ) : friends.length === 0 ? (
              <p className="create-bout-hint">You have no friends yet. Add friends first!</p>
            ) : (
              <select
                className="create-bout-select"
                value={selectedFriend}
                onChange={(e) => setSelectedFriend(e.target.value)}
              >
                <option value="">Select a friend...</option>
                {friends.map((f) => (
                  <option key={f.uid} value={f.username}>
                    {f.username}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Create Button */}
        <button
          className="btn-create-bout-submit"
          onClick={handleCreate}
          disabled={creating || (visibility === BOUT_VISIBILITY.PRIVATE && !selectedFriend)}
        >
          {creating ? 'Creating...' : 'Create Bout'}
        </button>
      </div>
    </div>
  );
}
